import { supabase } from './supabase'
import { getCurrentUserId } from './auth'
import type { AuditAction, EntityType, AuditLogParams } from '../types/audit'

export type { AuditAction, EntityType, AuditLogParams }

export async function createAuditLog(params: AuditLogParams): Promise<void> {
  try {
    let userId: string | null = null
    try {
      userId = await getCurrentUserId()
    } catch {
      // User may not be authenticated for some actions
    }

    const { error } = await supabase
      .from('audit_log')
      .insert({
        user_id: userId,
        action: params.action,
        entity_type: params.entityType,
        entity_id: params.entityId,
        old_data: params.oldData || null,
        new_data: params.newData || { ...params.metadata },
        ip_address: params.ipAddress || null,
        user_agent: params.userAgent || null,
      })

    if (error) {
      // Log error but don't throw - audit logging should not break main flow
      console.error('Audit log error:', error.message)
    }
  } catch (err) {
    // Silently fail for audit logging - never break the main operation
    console.error('Audit log error:', err instanceof Error ? err.message : 'Unknown error')
  }
}

// Convenience functions for common actions

export async function logIssueCreated(
  issueId: string,
  metadata: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'issue_created',
    entityType: 'issue',
    entityId: issueId,
    metadata,
  })
}

export async function logIssueStatusChange(
  issueId: string,
  oldStatus: string,
  newStatus: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'issue_status_changed',
    entityType: 'issue',
    entityId: issueId,
    oldData: { status: oldStatus },
    newData: { status: newStatus, ...metadata },
  })
}

export async function logCommentAdded(
  commentId: string,
  issueId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'comment_added',
    entityType: 'comment',
    entityId: commentId,
    metadata: { issue_id: issueId, ...metadata },
  })
}

export async function logDocumentServed(
  documentId: string,
  tenancyId: string,
  documentType: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'document_served',
    entityType: 'document',
    entityId: documentId,
    metadata: { tenancy_id: tenancyId, document_type: documentType, ...metadata },
  })
}

export async function logDocumentAcknowledged(
  documentId: string,
  ipAddress?: string,
  userAgent?: string
): Promise<void> {
  await createAuditLog({
    action: 'document_acknowledged',
    entityType: 'document',
    entityId: documentId,
    ipAddress,
    userAgent,
  })
}

export async function logDocumentDeleted(
  documentId: string,
  tenancyId: string,
  documentType: string,
  documentName: string,
  deletedBy: string
): Promise<void> {
  await createAuditLog({
    action: 'document_deleted',
    entityType: 'document',
    entityId: documentId,
    metadata: { tenancy_id: tenancyId, document_type: documentType, document_name: documentName, deleted_by: deletedBy },
  })
}

export async function logTenancyCreated(
  tenancyId: string,
  metadata: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'tenancy_created',
    entityType: 'tenancy',
    entityId: tenancyId,
    metadata,
  })
}

export async function logUserSuspended(userId: string): Promise<void> {
  await createAuditLog({
    action: 'user_suspended',
    entityType: 'user',
    entityId: userId,
  })
}

export async function logUserUnsuspended(userId: string): Promise<void> {
  await createAuditLog({
    action: 'user_unsuspended',
    entityType: 'user',
    entityId: userId,
  })
}

export async function logSettingUpdated(
  key: string,
  oldValue: unknown,
  newValue: unknown
): Promise<void> {
  await createAuditLog({
    action: 'setting_updated',
    entityType: 'setting',
    entityId: null,
    oldData: { key, value: oldValue },
    newData: { key, value: newValue },
  })
}

export async function logViewingScheduled(
  viewingId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'viewing_scheduled',
    entityType: 'viewing',
    entityId: viewingId,
    metadata,
  })
}

export async function logViewingConfirmed(
  viewingId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'viewing_confirmed',
    entityType: 'viewing',
    entityId: viewingId,
    metadata,
  })
}

export async function logViewingDeclined(
  viewingId: string,
  reason: string | null,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'viewing_declined',
    entityType: 'viewing',
    entityId: viewingId,
    metadata: { reason, ...metadata },
  })
}

export async function logViewingCompleted(
  viewingId: string,
  outcome: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'viewing_completed',
    entityType: 'viewing',
    entityId: viewingId,
    metadata: { outcome, ...metadata },
  })
}

export async function logViewingDeleted(
  viewingId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'viewing_deleted',
    entityType: 'viewing',
    entityId: viewingId,
    metadata,
  })
}

export async function logProspectArchived(
  prospectId: string,
  reason: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'prospect_archived',
    entityType: 'prospect',
    entityId: prospectId,
    metadata: { reason, ...metadata },
  })
}

export async function logProspectStageSkipped(
  prospectId: string,
  skippedStage: string,
  skippedToStage: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'prospect_stage_skipped',
    entityType: 'prospect',
    entityId: prospectId,
    metadata: {
      skipped_stage: skippedStage,
      skipped_to_stage: skippedToStage,
      reason: 'Landlord skipped stage',
      ...metadata,
    },
  })
}

export async function logCalendarEventCreated(
  eventId: string,
  metadata: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'calendar_event_created',
    entityType: 'calendar_event',
    entityId: eventId,
    metadata,
  })
}

export async function logCalendarEventUpdated(
  eventId: string,
  oldData: Record<string, unknown>,
  newData: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'calendar_event_updated',
    entityType: 'calendar_event',
    entityId: eventId,
    oldData,
    newData,
  })
}

export async function logCalendarEventDeleted(
  eventId: string,
  metadata: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'calendar_event_deleted',
    entityType: 'calendar_event',
    entityId: eventId,
    metadata,
  })
}

export async function logCalendarEventsSynced(
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'calendar_events_synced',
    entityType: 'calendar_event',
    entityId: null,
    metadata,
  })
}

// Portfolio audit logs

export async function logPortfolioCreated(
  portfolioId: string,
  metadata: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'portfolio_created',
    entityType: 'portfolio',
    entityId: portfolioId,
    metadata,
  })
}

export async function logPortfolioUpdated(
  portfolioId: string,
  oldData: Record<string, unknown>,
  newData: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'portfolio_updated',
    entityType: 'portfolio',
    entityId: portfolioId,
    oldData,
    newData,
  })
}

export async function logPortfolioArchived(
  portfolioId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'portfolio_archived',
    entityType: 'portfolio',
    entityId: portfolioId,
    metadata,
  })
}

export async function logPropertyAssignedToPortfolio(
  portfolioId: string,
  propertyId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'property_assigned_to_portfolio',
    entityType: 'portfolio',
    entityId: portfolioId,
    metadata: { property_id: propertyId, ...metadata },
  })
}

export async function logPropertyRemovedFromPortfolio(
  portfolioId: string,
  propertyId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'property_removed_from_portfolio',
    entityType: 'portfolio',
    entityId: portfolioId,
    metadata: { property_id: propertyId, ...metadata },
  })
}

// ============================================================
// TEAM MEMBER AUDIT HELPERS
// ============================================================

export async function logTeamMemberInvited(
  teamMemberId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'team_member_invited',
    entityType: 'team_member',
    entityId: teamMemberId,
    metadata,
  })
}

export async function logTeamMemberAccepted(
  teamMemberId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'team_member_accepted',
    entityType: 'team_member',
    entityId: teamMemberId,
    metadata,
  })
}

export async function logTeamMemberRevoked(
  teamMemberId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'team_member_revoked',
    entityType: 'team_member',
    entityId: teamMemberId,
    metadata,
  })
}

export async function logTeamMemberRoleChanged(
  teamMemberId: string,
  oldData?: Record<string, unknown>,
  newData?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'team_member_role_changed',
    entityType: 'team_member',
    entityId: teamMemberId,
    oldData,
    newData,
  })
}

export async function logTeamPortfolioAssigned(
  teamMemberId: string,
  portfolioId: string
): Promise<void> {
  await createAuditLog({
    action: 'team_portfolio_assigned',
    entityType: 'team_member',
    entityId: teamMemberId,
    metadata: { portfolio_id: portfolioId },
  })
}

export async function logTeamPortfolioUnassigned(
  teamMemberId: string,
  portfolioId: string
): Promise<void> {
  await createAuditLog({
    action: 'team_portfolio_unassigned',
    entityType: 'team_member',
    entityId: teamMemberId,
    metadata: { portfolio_id: portfolioId },
  })
}

// ============================================================
// SCHEDULED MAINTENANCE AUDIT HELPERS
// ============================================================

export async function logScheduledMaintenanceCreated(
  taskId: string,
  metadata: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'scheduled_maintenance_created',
    entityType: 'scheduled_maintenance_task',
    entityId: taskId,
    metadata,
  })
}

export async function logScheduledMaintenanceUpdated(
  taskId: string,
  oldData: Record<string, unknown>,
  newData: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'scheduled_maintenance_updated',
    entityType: 'scheduled_maintenance_task',
    entityId: taskId,
    oldData,
    newData,
  })
}

export async function logScheduledMaintenanceDeleted(
  taskId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'scheduled_maintenance_deleted',
    entityType: 'scheduled_maintenance_task',
    entityId: taskId,
    metadata,
  })
}

export async function logScheduledMaintenanceToggled(
  taskId: string,
  isActive: boolean,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'scheduled_maintenance_toggled',
    entityType: 'scheduled_maintenance_task',
    entityId: taskId,
    newData: { is_active: isActive, ...metadata },
  })
}

// ============================================================
// COMPLIANCE DOCUMENT AUDIT HELPERS
// ============================================================

export async function logComplianceDocumentUploaded(
  documentId: string,
  metadata: Record<string, unknown>,
): Promise<void> {
  await createAuditLog({
    action: 'compliance_document_uploaded',
    entityType: 'compliance_document',
    entityId: documentId,
    metadata,
  })
}

export async function logComplianceDocumentUpdated(
  documentId: string,
  oldData: Record<string, unknown>,
  newData: Record<string, unknown>,
): Promise<void> {
  await createAuditLog({
    action: 'compliance_document_updated',
    entityType: 'compliance_document',
    entityId: documentId,
    oldData,
    newData,
  })
}

export async function logComplianceDocumentDeleted(
  documentId: string,
  metadata: Record<string, unknown>,
): Promise<void> {
  await createAuditLog({
    action: 'compliance_document_deleted',
    entityType: 'compliance_document',
    entityId: documentId,
    metadata,
  })
}

export async function logComplianceDocumentArchived(
  documentId: string,
  metadata?: Record<string, unknown>,
): Promise<void> {
  await createAuditLog({
    action: 'compliance_document_archived',
    entityType: 'compliance_document',
    entityId: documentId,
    metadata,
  })
}

// ============================================================
// AGREEMENT AUDIT HELPERS
// ============================================================

// ============================================================
// TENANCY MESSAGE AUDIT HELPERS
// ============================================================

export async function logTenancyMessageSent(
  messageId: string,
  tenancyId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  await createAuditLog({
    action: 'tenancy_message_sent',
    entityType: 'tenancy_message',
    entityId: messageId,
    metadata: { tenancy_id: tenancyId, ...metadata },
  })
}
